<template>
  <div>
    <v-data-table :headers="currentHeaders"
                  :items="safetyObservations"
                  sort-by="lastName"
                  sort-desc
                  show-expand
                  single-expand
                  :mobile-breakpoint="dataTableMobileBreakpoint"
                  no-data-text="No safety observations on voyage"
                  :item-class="rowClassForItem">
      <template v-slot:top>
        <div class="d-flex pa-4">
          <span class="headline primary--text font-weight-medium">
            <v-icon class="primary--text" left>mdi-comment-alert</v-icon>Safety Observations
          </span>
          <v-spacer />
          <csv-download-button class="mx-2"
                               :size="headerButtonSize"
                               :data="safetyObservationCsvModel"
                               :fields="csvExportFields"
                               :labels="csvExportLabels"
                               :fileName="getExportFileName()" />
          <v-btn v-bind="headerButtonSize"
                 color="primary"
                 @click="openFormDialogToCreate">
            <v-icon left>mdi-plus</v-icon>Add
          </v-btn>
        </div>
      </template>
      <template v-slot:item.syncState="{ item }">
        <SyncStateIcon v-if="anyUnsyncedSafetyObservations" :isItemUnsynced="isSafetyObservationUnsynced(item)" />
      </template>
      <template v-slot:[`item.observationDateTimeLt`]="{ item }">
        <span>{{item.observationDateTimeLt | removeTAndSecondsAndFormatDate}}</span>
      </template>
      <template v-slot:item.photoFileNames="{ item }">
        <span class="no-wrap">
          <v-icon :size="iconSize"
                  :disabled="!isClientConnectedToServer"
                  @click="openPhotos(item)"
                  class="mx-2">mdi-file-image</v-icon>({{getPhotoCount(item)}})
        </span>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex">
          <v-icon :size="iconSize"
                  @click="openFormDialogToEdit(item)"
                  class="mx-2">mdi-pencil</v-icon>
          <v-icon :size="iconSize"
                  :disabled="!isClientConnectedToServer && !item.idc"
                  @click="openDeleteDialog(item)">mdi-delete</v-icon>
        </div>
      </template>
      <template v-slot:item.description="{ item }">
        <div class="text-truncate" style="max-width: 20rem">
          {{ item.description }}
        </div>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="text-start pa-4">
          <div class="my-2">
            <span class="font-weight-bold">Full description:</span>
            <div>{{ item.description }}</div>
          </div>
        </td>
      </template>
    </v-data-table>

    <!-- Photos Dialog -->
    <v-dialog v-model="photoDialog" max-width="950px">
      <v-card>
        <PhotoGallery ref="safetyObservationPhotoGallery"
                      :title="getPhotoGalleryTitle"
                      :photoFileNames="safetyObservation.photoFileNames"
                      :fileInfoGroup="safetyObservation.id"
                      :getPhotoFileUrl="getPhotoFileUrl"
                      :onDeletePhoto="deletePhoto"
                      :onUploadPhoto="uploadPhoto"
                      :onClose="closePhotos" />
      </v-card>
    </v-dialog>

    <!-- Form Dialog -->
    <v-dialog v-model="formDialog" max-width="950px">
      <v-card>
        <SafetyObservationForm ref="safetyObservationForm"
                       :safetyObservation="safetyObservation"
                       :onSubmitClick="submitSafetyObservation"
                       :onCloseButtonClick="closeFormDialog"
                       :isLoading="isSubmitting" />
      </v-card>
    </v-dialog>

    <!-- Confirm Dialog -->
    <ConfirmDialog v-model="deleteDialog"
                   title="Delete Safety Observaton"
                   description="Are you sure you want to delete this safety observation?"
                   :onConfirmClick="deleteSafetyObservation"
                   :onCloseClick="closeDeleteDialog" />

  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import dateTimeHelpers from '../../mixins/DateTimeHelpers';
import ResponsiveSizeHelperMixin from '../../mixins/ResponsiveSizeHelperMixin';
import SyncStateIcon from '../ui/SyncStateIcon.vue';
import CsvDownloadButton from '../ui/CsvDownloadButton.vue';
import SafetyObservationForm from './SafetyObservationForm.vue';
import ConfirmDialog from '../ui/ConfirmDialog.vue';
import PhotoGallery from '../ui/PhotoGallery.vue';

export default {
  name: 'SafetyObservations',
  mixins: [ResponsiveSizeHelperMixin],
  components: {
    SyncStateIcon,
    CsvDownloadButton,
    SafetyObservationForm,
    ConfirmDialog,
    PhotoGallery
  },
  props: {
    voyageReport: { type: Object, required: true }
  },
  data() {
    return {
      safetyObservation: {},
      tableHeaders: [
        { text: 'Observation Date', value: 'observationDateTimeLt' },
        { text: 'Reported By', value: 'reporterName' },
        { text: 'Location', value: 'location' },
        { text: 'Description', value: 'description' },
        { text: 'Non-conformance Type', value: 'nonConformanceType' },
        { text: 'Pictures', value: 'photoFileNames' },
        { text: '', value: 'actions' },
        { text: '', value: 'data-table-expand' }
      ],
      syncStateColumn: { text: 'Sync State', value: 'syncState' },
      csvExportFields: [
        'voyageReportNo',
        'observationDateTimeLt',
        'reporterName',
        'location',
        'description',
        'nonConformanceType',
        'photoCount'
      ],
      csvExportLabels: {
        voyageReportNo: 'Voyage Report',
        observationDateTimeLt: 'Observation Date (LT)',
        reporterName: 'Reporter',
        location: 'Location',
        description: 'Description',
        nonConformanceType: 'Non-conformance Type',
        photoCount: 'Pictures'
      },
      formDialog: false,
      isSubmitting: false,
      deleteDialog: false,
      photoDialog: false
    };
   },
  computed: {
    ...mapGetters({
      isClientConnectedToServer: 'userStore/isClientConnectedToServer',
      getSafetyObservationsForVoyage: 'safetyObservationsStore/getSyncedAndUnsyncedSafetyObservationsForVoyage',
      getPhotoFileUrlFromStore: 'safetyObservationsStore/getPhotoFileUrl'
    }),
    safetyObservations() {
      return this.getSafetyObservationsForVoyage(this.voyageReport.id ?? this.voyageReport.idc);
    },
    anyUnsyncedSafetyObservations() {
      return this.safetyObservations.some((x) => (x && x.idc));
    },
    currentHeaders() {
      let headers = this.tableHeaders;
      if (this.anyUnsyncedSafetyObservations) {
        headers = [
          this.syncStateColumn,
          ...this.tableHeaders,
        ];
      }
      return headers;
    },
    safetyObservationCsvModel() {
      return this.safetyObservations.map((x) => (
        {
          ...x,
          photoCount: this.getPhotoCount(x),
          voyageReportNo: this.voyageReport.voyageNo
        }
      ));
    },
    getPhotoGalleryTitle() {
      return `Safety Observations Photos`;
    }
  },
  methods: {
    isSafetyObservationUnsynced(safetyObservation) {
      return !!safetyObservation.idc;
    },
    rowClassForItem(item) {
      let rowClass = '';
      if ((this.anyUnsyncedSafetyObservations) && item) {
        rowClass = (this.isSafetyObservationUnsynced(item)) ? 'green lighten-4' : 'blue lighten-4';
      }

      return rowClass;
    },
    getExportFileName() {
      const voyageNo = this.voyageReport ? this.voyageReport.voyageNo : '';
      const dateTime = dateTimeHelpers.getFileTimestampNow();
      return `VoyageNo-${voyageNo}_SafetyObservations-${dateTime}.csv`;
    },
    openFormDialogToCreate() {
      this.safetyObservation = {};
      this.formDialog = true;
    },
    openFormDialogToEdit(safetyObservation) {
      this.safetyObservation = { ...safetyObservation };

      // split date and time to individual properties for date and time pickers
      var { observationDateTimeLt } = this.safetyObservation;
      if (observationDateTimeLt) {
        this.safetyObservation.date = dateTimeHelpers.getDateStringFromDateTime(observationDateTimeLt);
        this.safetyObservation.time = dateTimeHelpers.getTimeStringFromDateTime(observationDateTimeLt);
      }

      this.formDialog = true;
    },
    closeFormDialog() {
      this.safetyObservation = {};
      this.formDialog = false;
    },
    submitSafetyObservation() {
      this.isSubmitting = true;
      
      let safetyObservationModel = {
        ...this.safetyObservation,
        observationDateTimeLt: dateTimeHelpers.dateAndTimeLtString(this.safetyObservation.date, this.safetyObservation.time),
        voyageReportId: this.voyageReport.id,
        voyageReportIdc: this.voyageReport.idc
      };

      let storeAction = this.safetyObservation.id || this.safetyObservation.idc
        ? 'safetyObservationsStore/updateSafetyObservation'
        : 'safetyObservationsStore/createSafetyObservation'

      this.$store.dispatch(storeAction, safetyObservationModel)
        .then(resp => {
          this.$toast.success(resp.message);
          this.closeFormDialog();
        })
        .catch(err => this.$toast.error(err.message))
        .finally(() => this.isSubmitting = false);
    },
    openDeleteDialog(safetyObservation) {
      this.safetyObservation = { ...safetyObservation };
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.safetyObservation = {};
      this.deleteDialog = false;
    },
    deleteSafetyObservation() {
      return this.$store.dispatch('safetyObservationsStore/deleteSafetyObservation', this.safetyObservation)
        .then((result) => this.$toast.success(result.message))
        .then(() => this.closeDeleteDialog())
        .catch((err) => this.onError(err.message, err.data));
    },
    getPhotoCount(safetyObservation) {
      return safetyObservation.photoFileNames ? safetyObservation.photoFileNames.length : 0;
    },
    openPhotos(safetyObservation) {
      this.safetyObservation = safetyObservation; // Don't clone it - just read access to photoFileNames, which may change while dialog open
      this.photoDialog = true;
    },
    closePhotos() {
      this.safetyObservation = {};
      this.photoDialog = false;
    },
    getPhotoFileUrl(filename) {
      return this.getPhotoFileUrlFromStore(filename, this.safetyObservation.id)
    },
    uploadPhoto(file) {
      return this.$store.dispatch('safetyObservationsStore/uploadPhoto', { file, safetyObservationId: this.safetyObservation.id })
        .then(() => {
          // this.safetyObservation is local state, not in the store,
          // and not refreshed by the store refresh of server data (needed for any server modified file names)
          // Should be moved into the store, but this is a stop gap to refresh the PhotoFileNames locally.
          var so = this.safetyObservations.find(so => so.id === this.safetyObservation.id)
          for (let pfn of (so.photoFileNames || [])) {
            if (!this.safetyObservation.photoFileNames.includes(pfn)) {
              this.safetyObservation.photoFileNames.push(pfn)
            }
          }          
        });
    },
    deletePhoto(filename) {
      return this.$store.dispatch('safetyObservationsStore/deletePhoto', { filename, safetyObservationId: this.safetyObservation.id })
        .then(() => {
          // this.safetyObservation is local state, not in the store,
          // and not refreshed by the store refresh of server data (needed for any server modified file names)
          // Should be moved into the store, but this is a stop gap to refresh the PhotoFileNames locally.
          const i = this.safetyObservation.photoFileNames.findIndex(pfn => pfn === filename);
          if (i !== -1) {
            this.safetyObservation.photoFileNames.splice(i, 1);
          }
        });
    }
  },
  created() {
    this.$store.dispatch('safetyObservationsStore/loadSafetyObservationsForVoyage', this.voyageReport.id);
  },
  beforeDestroy() {
    this.$store.dispatch('safetyObservationsStore/clearSafetyObservations');
  },
};
</script>

<style>
  .no-wrap {
    overflow: hidden;
    white-space: nowrap;
  }
</style>
